"use client"

import { router } from "@inertiajs/react"
import { Archive, CheckCircle, Trash01 } from "@untitledui/icons"
import { useState } from "react"

import {
  DestinationKindBadge,
  DestinationStatusBadge,
} from "@/components/admin/onwards/destination-badges"
import { ButtonUtility } from "@/components/ui/button-utility"
import { DestructiveStackedLeftAlignedModal } from "@/components/ui/destructive-stacked-left-aligned-modal"
import { Table, TableCard } from "@/components/ui/table"
import {
  adminOnwardsDestinationPath,
  archiveAdminOnwardsDestinationPath,
  editAdminOnwardsDestinationPath,
  validateAdminOnwardsDestinationPath,
} from "@/routes"

export interface DestinationRow {
  id: number
  param: string
  kind: string
  name: string
  key: string
  status: string
  summary: string | null
  action_label: string | null
  placements_count: number
  image_url: string | null
}

interface DestinationsTableProps {
  rows: DestinationRow[]
  "aria-label"?: string
  footer?: React.ReactNode
}

// The onward-destination catalogue. Mirrors the admin userbase table: the whole
// row opens the destination; the right-hand icons are the governance actions.
export function DestinationsTable({
  rows,
  "aria-label": ariaLabel = "Onward destinations",
  footer,
}: DestinationsTableProps) {
  const [deleting, setDeleting] = useState<DestinationRow | null>(null)

  return (
    <>
      <TableCard.Root>
        <Table aria-label={ariaLabel} className="bg-primary">
          <Table.Header>
            <Table.Head id="name" isRowHeader label="Name" className="w-full" />
            <Table.Head id="kind" label="Kind" />
            <Table.Head id="status" label="Status" />
            <Table.Head id="placements" label="On" className="text-right" />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <Table.Body items={rows}>
            {(row: DestinationRow) => (
              <Table.Row
                id={row.param}
                className="cursor-pointer"
                onAction={() =>
                  router.visit(editAdminOnwardsDestinationPath(row.param))
                }
              >
                <Table.Cell>
                  <div className="flex items-center gap-3">
                    {row.image_url ? (
                      <img
                        src={row.image_url}
                        alt=""
                        className="h-8 w-14 shrink-0 rounded-[2px] object-cover"
                      />
                    ) : (
                      <div className="bg-secondary h-8 w-14 shrink-0 rounded-[2px]" />
                    )}
                    <div className="flex flex-col">
                      <span className="text-primary text-sm font-medium">
                        {row.name}
                      </span>
                      <span className="text-tertiary text-xs">{row.key}</span>
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <DestinationKindBadge kind={row.kind} />
                </Table.Cell>
                <Table.Cell>
                  <DestinationStatusBadge status={row.status} />
                </Table.Cell>
                <Table.Cell className="text-tertiary text-right tabular-nums">
                  {row.placements_count}
                </Table.Cell>
                <Table.Cell>
                  <div className="flex justify-end gap-0.5">
                    {row.status !== "validated" && (
                      <ButtonUtility
                        size="sm"
                        color="tertiary"
                        icon={CheckCircle}
                        tooltip="Validate"
                        onClick={() =>
                          router.patch(
                            validateAdminOnwardsDestinationPath(row.param),
                          )
                        }
                      />
                    )}
                    {row.status !== "archived" && (
                      <ButtonUtility
                        size="sm"
                        color="tertiary"
                        icon={Archive}
                        tooltip="Archive"
                        onClick={() =>
                          router.patch(
                            archiveAdminOnwardsDestinationPath(row.param),
                          )
                        }
                      />
                    )}
                    <ButtonUtility
                      size="sm"
                      color="tertiary"
                      icon={Trash01}
                      tooltip="Delete"
                      onClick={() => setDeleting(row)}
                    />
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        {footer}
      </TableCard.Root>

      <DestructiveStackedLeftAlignedModal
        isOpen={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={deleting ? `Delete ${deleting.name}?` : "Delete destination?"}
        description="This removes the destination and any placements that use it. Archive it instead if you only want to keep it out of live slates."
        confirmLabel="Delete"
        onConfirm={() =>
          deleting && router.delete(adminOnwardsDestinationPath(deleting.param))
        }
      />
    </>
  )
}
